import { DomainStore } from "./domainStore";
import { observable, runInAction, action } from "mobx";
import {
  sendLogin,
  checkLogin,
  sendGoogleLogin,
} from "./persistence/persistLogin";
import { toastError } from "../domain/errorHandling/toaster";
import Cookies from "js-cookie";
import { ITeamMember } from "encharge-domain/definitions/TeamMember";
import { segmentIdentify } from "domain/helpers/segment.io";
import { redirectWithReload } from "domain/helpers/redirect";
import {
  confirmLoginErrorMessage,
  confirmEmailErrorMessage,
} from "encharge-domain/lib/helpers/login_helper";

export class AuthStore {
  rootStore: DomainStore;
  constructor(rootStore: DomainStore) {
    this.rootStore = rootStore;
    this.loggedIn = true;
    // readonly, don't try to login
    if (
      this.rootStore.permissionsStore.readOnlyFlow ||
      window.location.pathname.includes("/view-form")
    )
      return;
    this.tryLogin();
  }

  @observable
  loggedIn?: boolean;

  @action
  async logOut() {
    Cookies.remove("connect.sid", { path: "/", domain: ".encharge.io" });
    // if domain is localhost, remove localhost cookie too
    if (window.location.hostname === "localhost") {
      Cookies.remove("connect.sid", { path: "/", domain: "localhost" });
    }
    this.loggedIn = false;
    // redirect to login
    redirectWithReload("/login");
  }

  @action
  async logIn(credentials: {
    email?: string;
    password?: string;
    googleToken?: string;
  }) {
    const uiStore = this.rootStore.uiStore;
    let teamMember: ITeamMember | undefined;
    try {
      if (credentials.email && credentials.password) {
        uiStore.loggingIn.loading = true;
        uiStore.loggingIn.loadingGoogleSignin = false;
        uiStore.loggingIn.showLogin = true;
        uiStore.loggingIn.error = undefined;

        teamMember = await sendLogin(credentials.email, credentials.password);
      } else if (credentials.googleToken) {
        uiStore.loggingIn.loadingGoogleSignin = true;
        uiStore.loggingIn.loading = false;
        uiStore.loggingIn.showLogin = true;
        uiStore.loggingIn.error = undefined;

        teamMember = await sendGoogleLogin(credentials.googleToken);
      } else {
        throw new Error("Missing email or password.");
      }
      if (teamMember) {
        await segmentIdentify({
          accountId: teamMember.accountId,
          teamMemberId: teamMember.id,
          traits: { email: teamMember.email },
        });
      }

      // All good, we are logged in. Save in state and go on our merrily way.
      runInAction(() => {
        uiStore.loggingIn.loading = false;
        uiStore.loggingIn.loadingGoogleSignin = false;
        uiStore.loggingIn.redirect = true;
        uiStore.loggingIn.showLogin = false;
        uiStore.loggingIn.error = undefined;

        this.loggedIn = true;
      });
    } catch (e) {
      toastError({
        message: "We couldn't log you in.",
        extra: e,
      });

      runInAction(() => {
        uiStore.loggingIn.loading = false;
        uiStore.loggingIn.loadingGoogleSignin = false;
        uiStore.loggingIn.showLogin = true;
        const message = (e as any)?.response?.error?.message || e.message;
        if (message === confirmLoginErrorMessage) {
          uiStore.loggingIn.error = confirmLoginErrorMessage;
        } else if (message === confirmEmailErrorMessage) {
          // Unconfirmed signup email. login() has already (re)sent the
          // confirmation link, so just surface the instruction.
          uiStore.loggingIn.error = confirmEmailErrorMessage;
        }
      });
    }
  }

  async tryLogin() {
    try {
      await checkLogin();
      runInAction(() => {
        this.loggedIn = true;
      });
    } catch (error) {
      // unauthorized
      if (error.response && error.response.status === 401) {
        runInAction(() => {
          this.loggedIn = false;
        });
      } else {
        // TODO
        // EN-783 Check for internet connectivity and display retry overlay
        toastError(
          "We are having issues connecting to our servers. Please check your internet connection and reload."
        );
      }
    }
  }
}
